import Vue from 'vue';

import { initialize } from '../../../util/sentry';
initialize(Vue);

import imageOverlayZoomComponent from '../../../components/common/imageOverlayZoom.vue';
import shoppingCartApi from '../../../api/shoppingcarts';
import { Currency } from '../../../util/mixin/currency.mixin';

// 商品タイプ
const PRODUCT_TYPES = {
  TSHIRT: 'TSHIRT',
  TOTEBAG: 'TOTEBAG'
};

// 商品カラー（Tシャツのカラーは現状白・黒のみ）
const PRODUCT_COLORS = {
  TSHIRT: {
    WHITE: 'WHITE',
    NAVY: 'NAVY',
    BLACK: 'BLACK',
    GRAY: 'GRAY'
  },
  TOTEBAG: {
    NATURAL: 'NATURAL'
  }
};

// 商品サイズ
const PRODUCT_SIZES = {
  TSHIRT: {
    S: 'S',
    M: 'M',
    L: 'L',
    XL: 'XL',
    XXL: 'XXL',
    XXXL: 'XXXL'
  },
  TOTEBAG: {
    ML: 'ML'
  }
};

// 商品画像（トートバッグのみ）
const PRODUCT_TOTEBAG_IMAGES = [
  {
    name: 'image sample'
  },
  {
    name: '単ページ',
    filename: 'tote_img_01.jpg'
  },
  {
    name: '見開き',
    filename: 'tote_img_02.jpg'
  },
  {
    name: 'カラー',
    filename: 'tote_img_03.jpg'
  },
  {
    name: '拡大（絵柄）',
    filename: 'tote_img_04.jpg'
  },
  {
    name: '拡大（生地）',
    filename: 'tote_img_05.jpg'
  }
];

const MAX_PURCHASE_QTY = 6;

new Vue({
  el: '#vue-app',

  mixins: [Currency],

  props: {
    name: String,
    description: String,
    copyright: String,
    page: Number,
    skus: Array,
    hasEnglishTitle: Boolean
  },

  propsData: window.propsData,

  data() {
    return {
      navOpen: false,
      location: '#main',
      selectedProductType: PRODUCT_TYPES.TSHIRT,
      selectedProductColor: PRODUCT_COLORS.TSHIRT.WHITE,
      selectedProductSize: PRODUCT_SIZES.TSHIRT.S,
      selectedProductImageIndex: 0,
      qty: 1
    };
  },

  components: {
    'vue-image-overlay-zoom': imageOverlayZoomComponent
  },

  computed: {
    // 商品タイプ定数
    PRODUCT_TYPES() {
      return PRODUCT_TYPES;
    },

    // 商品画像（トートバッグのみ）
    PRODUCT_TOTEBAG_IMAGES() {
      return PRODUCT_TOTEBAG_IMAGES;
    },

    // 作品名
    productionTitle() {
      return `[Japanese Ver.] ${this.description}`;
    },

    // コンテンツ名（ページ名を含むタイトルを取得）
    contentTitle() {
      return this.hasEnglishTitle
        ? this.name
        : `${this.name} Page ${this.page}`;
    },

    // 選択済みSKUオブジェクト
    selectedProductSku() {
      let skuNo = '';

      if (!this.selectedProductType) {
        return null;
      }

      if (this.selectedProductType === PRODUCT_TYPES.TOTEBAG) {
        skuNo = PRODUCT_TYPES.TOTEBAG;
      } else {
        skuNo = `${this.selectedProductType}-${this.selectedProductColor}-${this.selectedProductSize}`;
      }

      return this.skus.find((sku) => sku.no === skuNo);
    },

    // 選択SKU価格のオブジェクトを返却
    selectedProductSkuPriceObject() {
      if (!this.selectedProductSku) {
        return {};
      }
      const symbol = '$';
      const decimal = '.';
      const priceArr = this.selectedProductSku.available_price
        .toFixed(2)
        .split(decimal);
      return {
        surface: Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(this.selectedProductSku.available_price),
        symbol,
        decimal,
        whole: Intl.NumberFormat().format(priceArr[0]).toString(),
        fraction: priceArr[1].toString()
      };
    },

    // 選択可能カラーリスト
    colorVariations() {
      if (this.selectedProductType === PRODUCT_TYPES.TSHIRT) {
        return PRODUCT_COLORS.TSHIRT;
      }
      return PRODUCT_COLORS.TOTEBAG;
    },

    // 選択可能サイズリスト
    sizeVariations() {
      if (this.selectedProductType === PRODUCT_TYPES.TSHIRT) {
        return PRODUCT_SIZES.TSHIRT;
      }
      return PRODUCT_SIZES.TOTEBAG;
    },

    // 選択済み商品画像
    selectedProductImage() {
      if (this.selectedProductType === PRODUCT_TYPES.TSHIRT) {
        return null;
      }
      return this.PRODUCT_TOTEBAG_IMAGES[this.selectedProductImageIndex];
    },

    // 最大購入個数
    maxPurchaseQty() {
      return MAX_PURCHASE_QTY;
    }
  },

  methods: {
    // メニューナビ開閉
    onNavToggle() {
      this.navOpen = !this.navOpen;

      document.querySelector('html').classList.toggle('is-fixed');
      this.goto('#main');
    },

    onOpenTshirtSizeChart() {
      this.onNavToggle();
      this.goto('#tshirt_size_chart');
    },

    // 商品タイプ選択
    onSelectProductType(type) {
      this.selectedProductType = type;

      switch (this.selectedProductType) {
        case PRODUCT_TYPES.TSHIRT:
          this.selectedProductColor = PRODUCT_COLORS.TSHIRT.WHITE;
          this.selectedProductSize = PRODUCT_SIZES.TSHIRT.S;
          this.selectedProductImageIndex = 0;
          break;
        case PRODUCT_TYPES.TOTEBAG:
          this.selectedProductColor = PRODUCT_COLORS.TOTEBAG.NATURAL;
          this.selectedProductSize = PRODUCT_SIZES.TOTEBAG.ML;
          break;
        default:
          this.selectedProductColor = '';
          this.selectedProductSize = '';
          break;
      }
    },

    // 商品イメージ選択（トートバッグのみ）
    onSelectProductImage(index) {
      if (
        !(
          this.selectedProductType &&
          this.selectedProductType === PRODUCT_TYPES.TOTEBAG
        )
      ) {
        return;
      }
      this.selectedProductImageIndex = index;
    },

    // 商品カラー選択
    onSelectColor(color) {
      if (!this.selectedProductType) {
        return;
      }

      if (!PRODUCT_COLORS[this.selectedProductType][color]) {
        return;
      }
      this.selectedProductColor = color;
    },

    // 商品サイズ選択
    onSelectSize(size) {
      if (!this.selectedProductType) {
        return;
      }

      if (!PRODUCT_SIZES[this.selectedProductType][size]) {
        return;
      }
      this.selectedProductSize = size;
    },

    // カートに入れる
    addToCart($event) {
      $event.preventDefault();
      const form = this.$refs.addToCartForm;
      const product_id = form.querySelector('input[name="product_id"]').value;
      const sku_id = form.querySelector('input[name="sku_id"]').value;
      const variation_id = form.querySelector(
        'input[name="variation_id"]'
      ).value;
      // 詳細画面を開けることの証明として、詳細画面の URL の id・page・h を送る
      const mangaplus_id = form.querySelector(
        'input[name="mangaplus_id"]'
      ).value;
      const mangaplus_page = form.querySelector(
        'input[name="mangaplus_page"]'
      ).value;
      const mangaplus_h = form.querySelector('input[name="mangaplus_h"]').value;

      shoppingCartApi.addToCart(
        {
          product_id,
          sku_id,
          variation_id,
          mangaplus_id,
          mangaplus_page,
          mangaplus_h
        },
        (err) => {
          if (err) {
            return window.alert(err.message || 'An error occurred.');
          }

          return (window.location.href = '/cart');
        }
      );
    },

    // staticページ表示
    goto(location) {
      this.location = location;
    }
  }
});
